import { IconButton } from '@material-ui/core';
import FeedbackIcon from '@material-ui/icons/Feedback';
import PersonIcon from '@material-ui/icons/Person';
import PublicIcon from '@material-ui/icons/Public';
import RefreshIcon from '@material-ui/icons/Refresh';
import { Alert } from '@material-ui/lab';
import clsx from 'clsx';
import Link from 'components/Link';
import { LayoutContext } from 'components/RefreshContext/LayoutContext';
import { SubModuleTabs } from 'components/SubModuleTabs';
import { APP_MODULES } from 'constants/appModules';
import { NOT_WELL_RELATED_PATHS, PATHS } from 'constants/paths';
import { getUnifiedWellName } from 'helpers/WellHelper';
import { getModuleFromPath, getPageNameFromPath, getTabsModuleFromPath } from 'helpers/pathParser';
import { isNotNullNorUndefined } from 'helpers/ts-helpers';
import { MouseEvent, MutableRefObject, useContext, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { matchPath, NavLink, useParams, useRouteMatch } from 'react-router-dom';
import { useSelectCurrentWell, useSelectCurrentWellbore } from 'redux/Wells/selectors';
import { WellFullDetailsDTO } from 'types/well';

import { useActiveWebIncidents } from './Header.hooks';
import styles from './Header.module.scss';
import HeaderMobile from './components/HeaderMobile';
import OnboardingToggle from './components/OnboardingToggle';
import ProfilePopup from './components/ProfilePopup';
import { getTabsByCurrentModule, MODULES_WITH_REFRESH } from './utils';

type Props = {
  isMobile?: boolean;
  withMobileWellSelect?: boolean;
  setSidebarOpen: (openSiderBar: boolean) => void;
};

const Header = ({ isMobile, withMobileWellSelect, setSidebarOpen }: Props) => {
  const { setRefreshLessonLearnt } = useContext(LayoutContext);
  const intl = useIntl();
  const match = useRouteMatch();
  const currentWellbore = useSelectCurrentWellbore();
  const buttonElementRef = useRef<HTMLButtonElement>(null) as MutableRefObject<HTMLButtonElement>;

  const currentTabsModule = useMemo(() => getTabsModuleFromPath(match.path), [match.path]);
  const currentPage = useMemo(() => getPageNameFromPath(match.path), [match.path]);
  const currentModule = getModuleFromPath(match.path);
  const isShowRefresh = MODULES_WITH_REFRESH.includes(currentModule);

  const isWellNameDisplayed = useMemo(
    () => !NOT_WELL_RELATED_PATHS.some(path => matchPath(match.path, { path, exact: true })),
    [match.path],
  );

  const currentWell: WellFullDetailsDTO | undefined = useSelectCurrentWell();
  const [isProfilePopupDisplayed, setProfilePopupDisplayed] = useState(false);

  const { activeWebIncidents, hideWebIncident } = useActiveWebIncidents();

  const headerTabs = useMemo(() => {
    return getTabsByCurrentModule(currentTabsModule);
  }, [currentTabsModule]);

  const { wellPlanId: wellPlanIdFromPath } = useParams<{ wellPlanId?: string }>();

  const toggleProfilePopup = () => {
    setProfilePopupDisplayed(prevState => !prevState);
  };

  const hideProfilePopup = (event: MouseEvent<EventTarget>) => {
    if (
      buttonElementRef.current &&
      buttonElementRef.current.contains(event.target as HTMLElement)
    ) {
      return;
    }
    setProfilePopupDisplayed(false);
  };

  if (isMobile) {
    return <HeaderMobile withMobileWellSelect={withMobileWellSelect} />;
  }

  const getPageName = () => {
    if (isNotNullNorUndefined(currentTabsModule)) {
      return currentTabsModule;
    } else if (currentPage && currentPage !== null) {
      return currentPage;
    }
  };

  const onClickRefereshIcon = () => {
    if (currentModule === APP_MODULES.LESSONS_LEARNT) {
      setRefreshLessonLearnt(true);
    }
  };

  return (
    <>
      {activeWebIncidents?.length > 0 && (
        <div className={styles.webIncidentsContainer}>
          {activeWebIncidents.map(incident => (
            <Alert
              key={incident.id}
              severity={incident.severity}
              onClose={() => hideWebIncident(incident)}
            >
              {incident.message}
            </Alert>
          ))}
        </div>
      )}
      <header className={clsx(styles.headerContainer, headerTabs && styles.withTabs)}>
        <ProfilePopup
          anchorRef={buttonElementRef}
          isOpen={isProfilePopupDisplayed}
          onHideProfilePopupClick={hideProfilePopup}
        />
        <div className={styles.headerInfoContainer}>
          <div className={styles.wellboreInfoAndQualityContainer}>
            <div className={styles.pageTitleAndOnboardingToggle}>
              <div className={styles.wellboreInfoContainer}>
                {currentWell && isWellNameDisplayed && (
                  <span>{getUnifiedWellName(currentWell)}</span>
                )}
                {getPageName() !== APP_MODULES.USER_ADMIN && (
                  <span className={styles.moduleTitle}>
                    {intl.formatMessage({ id: `header.pages.${currentTabsModule ?? currentPage}` })}
                  </span>
                )}
              </div>
              <OnboardingToggle currentPage={currentPage} />
            </div>
          </div>
          <div className={styles.linksContainer}>
            {isShowRefresh && (
              <div className={styles.roundButtonContainer}>
                <IconButton
                  data-testid="refresh"
                  onClick={() => onClickRefereshIcon()}
                  className={styles.roundButton}
                >
                  <RefreshIcon className={styles.icon} />
                </IconButton>

                <div className={styles.roundButtonLabel}>
                  {intl.formatMessage({ id: `header.icons.refresh` })}
                </div>
              </div>
            )}

            <div className={styles.roundButtonContainer}>
              <IconButton
                data-testid="profile"
                ref={buttonElementRef}
                className={clsx(styles.roundButton, isProfilePopupDisplayed && styles.active)}
                onClick={toggleProfilePopup}
              >
                <PersonIcon className={styles.icon} />
              </IconButton>
              <div
                className={clsx(styles.roundButtonLabel, isProfilePopupDisplayed && styles.active)}
              >
                {intl.formatMessage({ id: `header.icons.profile` })}
              </div>
            </div>
            <div className={styles.roundButtonContainer}>
              <NavLink
                exact
                activeClassName={styles.activeLink}
                className={styles.cockpitLink}
                to={PATHS.DRILLX_COCKPIT}
                tabIndex={-1}
              >
                <IconButton className={styles.roundButton}>
                  <PublicIcon className={styles.icon} />
                </IconButton>
              </NavLink>
              <div className={styles.roundButtonLabel}>
                {intl.formatMessage({ id: `header.icons.map` })}
              </div>
            </div>
            <div className={styles.roundButtonContainer}>
              <Link to={PATHS.GLOBAL_FEEDBACK} className={styles.cockpitLink} tabIndex={-1}>
                <IconButton className={styles.roundButton}>
                  <FeedbackIcon className={styles.icon} />
                </IconButton>
              </Link>
              <div className={styles.roundButtonLabel}>
                {intl.formatMessage({ id: `header.icons.feedback` })}
              </div>
            </div>
          </div>
        </div>
        {currentTabsModule && headerTabs && (
          <SubModuleTabs
            tabModule={currentTabsModule}
            subModules={headerTabs}
            pathParams={{
              tuwi: currentWell?.tuwi,
              wellboreId: currentWellbore?.wellboreId,
              wellPlanId: wellPlanIdFromPath,
            }}
            setSidebarOpen={setSidebarOpen}
          />
        )}
      </header>
    </>
  );
};

export default Header;
